import {gql} from '@apollo/client';
import React from 'react';

import {TagSchemasSSIQuery} from 'bulk-import/panes/__generated/TagSchemasSSIQuery';
import EntitySchemaSelection, {EntitySchemaSelectionProps} from 'bulk-import/panes/EntitySchemaSelection';
import {TagSchema} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';

export const TAG_SCHEMAS_SSI_QUERY = gql`
  query TagSchemasSSIQuery @skipAudit @engComponent(name: REGISTRY) {
    tagSchemas {
      id
      interfaceConfigs {
        interface {
          id
          ... on StaticSchemaInterfaceNode {
            id
          }
        }
      }
    }
  }
`;

interface EntitySchemaSSISelectionWithIncludeRestrictionsProps extends EntitySchemaSelectionProps {
  includedInterfaceIds: Array<string>;
  excludedInterfaceIds?: never;
}

interface EntitySchemaSSISelectionWithExcludeRestrictionsProps extends EntitySchemaSelectionProps {
  includedInterfaceIds?: never;
  excludedInterfaceIds: Array<string>;
}

/**
 * Entity schema selector dropdown, with some bells and whistles provided by `EntitySchemaSelection`, that
 * filters to schemas implementing a particular system fieldset (aka static schema interface / SSI).
 */
export default function EntitySchemaSSISelectionWithRestrictions(
  props:
    | EntitySchemaSSISelectionWithIncludeRestrictionsProps
    | EntitySchemaSSISelectionWithExcludeRestrictionsProps
): JSX.Element {
  return (
    <BenchlingQuery<TagSchemasSSIQuery>
      query={TAG_SCHEMAS_SSI_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {(data) => {
        const interfaceIdsBySchemaId = Object.fromEntries(
          data.tagSchemas.map((schema) => [
            schema.id,
            schema.interfaceConfigs.map((interfaceConfig) => interfaceConfig.interface.id),
          ])
        );

        const tagSchemaFilterFn = (schema: TagSchema): boolean => {
          const schemaInterfaceIds = interfaceIdsBySchemaId[schema.id] ?? [];

          if (props.includedInterfaceIds != null) {
            return props.includedInterfaceIds.some((id) => schemaInterfaceIds.includes(id));
          } else {
            return !props.excludedInterfaceIds.some((id) => schemaInterfaceIds.includes(id));
          }
        };

        return <EntitySchemaSelection {...props} tagSchemaFilterFn={tagSchemaFilterFn} />;
      }}
    </BenchlingQuery>
  );
}
